import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useUnits } from '../contexts/UnitsContext';
import { formatWeight } from '../utils/units';
import './PlateCalculator.css';

// Standard plate sizes in lbs and kg
const PLATE_SIZES_LBS = [45, 35, 25, 10, 5, 2.5];
const PLATE_SIZES_KG = [25, 20, 15, 10, 5, 2.5, 1.25];

// Bar types with weights in both units
const BAR_TYPES = [
  { id: 'olympic', name: 'Olympic Bar', lbs: 45, kg: 20 },
  { id: 'olympic-women', name: "Women's Olympic", lbs: 33, kg: 15 },
  { id: 'ez-curl', name: 'EZ Curl Bar', lbs: 18, kg: 8 },
  { id: 'trap', name: 'Trap/Hex Bar', lbs: 45, kg: 20 },
  { id: 'smith', name: 'Smith Machine', lbs: 20, kg: 9 },
  { id: 'fixed', name: 'Fixed Barbell', lbs: 0, kg: 0 },
  { id: 'none', name: 'No Bar', lbs: 0, kg: 0 },
];

/**
 * Calculate the number of each plate needed to reach a target weight.
 * Prefers balanced pairs of plates (2, 4, 6...) for barbell exercises.
 * Only uses odd plate counts when pairs can't reach the exact target.
 * @param {number} totalWeight - Total target weight
 * @param {string} unit - 'lbs' or 'kg'
 * @param {number} barWeight - Weight of the bar
 * @returns {Object} - { plates: { size: count }, remainingWeight }
 */
const calculatePlates = (totalWeight, unit, barWeight) => {
  const plateSizes = unit === 'kg' ? PLATE_SIZES_KG : PLATE_SIZES_LBS;
  
  // Weight to distribute on plates
  let remainingWeight = Math.max(0, totalWeight - barWeight);
  
  // First pass: try to use only pairs (balanced for both sides of barbell)
  const platesWithPairs = {};
  let remainingWithPairs = remainingWeight;
  
  for (const size of plateSizes) {
    // Calculate how many pairs we can use (each pair = 2 plates = 2*size weight)
    const pairsNeeded = Math.floor(remainingWithPairs / (size * 2));
    if (pairsNeeded > 0) {
      platesWithPairs[size] = pairsNeeded * 2; // Store as individual plate count
      remainingWithPairs -= pairsNeeded * size * 2;
    }
  }
  
  // Round to avoid floating point issues
  remainingWithPairs = Math.round(remainingWithPairs * 100) / 100;
  
  // If pairs got us to exact weight (or close enough), use that
  if (remainingWithPairs === 0) {
    return { plates: platesWithPairs, remainingWeight: 0 };
  }
  
  // Second pass: try to fill the gap with single plates if needed
  // Start from the pairs result and add singles for the remainder
  const plates = { ...platesWithPairs };
  let finalRemaining = remainingWithPairs;
  
  for (const size of plateSizes) {
    if (finalRemaining >= size) {
      const singlesNeeded = Math.floor(finalRemaining / size);
      if (singlesNeeded > 0) {
        plates[size] = (plates[size] || 0) + singlesNeeded;
        finalRemaining -= singlesNeeded * size;
      }
    }
  }
  
  // Round remaining weight to avoid floating point issues
  finalRemaining = Math.round(finalRemaining * 100) / 100;
  
  return { plates, remainingWeight: finalRemaining };
};

/**
 * Calculate total weight from plate counts and bar weight
 */
const calculateTotalFromPlates = (plates, barWeight) => {
  let total = barWeight;
  
  for (const [size, count] of Object.entries(plates)) {
    total += parseFloat(size) * count; // Each plate adds its weight
  }
  
  return total;
};

/**
 * PlateCalculator Modal Component
 * Opens as a modal via modalService.open(PlateCalculator, { ... })
 * Props:
 *   - initialValue: The current weight value
 *   - onAccept: Callback when user accepts changes (receives new weight value)
 *   - close: Modal close function (provided by modalService)
 *   - storageKey: Optional key for localStorage persistence
 */
const PlateCalculator = ({ 
  initialValue, 
  onAccept, 
  close,
  storageKey = null,
  onClose = null // Callback to notify parent when modal closes
}) => {
  const { weightUnit } = useUnits();
  const plateSizes = weightUnit === 'kg' ? PLATE_SIZES_KG : PLATE_SIZES_LBS;
  
  // Bar type state
  const [selectedBarId, setSelectedBarId] = useState('olympic');
  
  // Local plate state for when user adjusts via buttons
  const [localPlates, setLocalPlates] = useState({});
  
  // Get current bar weight based on selection and unit
  const selectedBar = BAR_TYPES.find(b => b.id === selectedBarId) || BAR_TYPES[0];
  const barWeight = weightUnit === 'kg' ? selectedBar.kg : selectedBar.lbs;
  
  // Convert the incoming value to current display unit if needed
  const currentWeight = parseFloat(initialValue) || 0;
  
  // Generate storage key for this weight field
  const fullStorageKey = storageKey ? `plateCalc_${storageKey}_${weightUnit}` : null;
  
  // Initialize on mount
  useEffect(() => {
    let loaded = false;
    
    // Try to load from localStorage
    if (fullStorageKey) {
      try {
        const saved = localStorage.getItem(fullStorageKey);
        if (saved) {
          const config = JSON.parse(saved);
          if (config.plates && config.barId) {
            setLocalPlates(config.plates);
            setSelectedBarId(config.barId);
            loaded = true;
          }
        }
      } catch (e) {
        // Ignore localStorage errors
      }
    }
    
    // If nothing saved, calculate initial breakdown
    if (!loaded) {
      // Need to get bar weight for initial calculation
      const defaultBar = BAR_TYPES.find(b => b.id === 'olympic') || BAR_TYPES[0];
      const defaultBarWeight = weightUnit === 'kg' ? defaultBar.kg : defaultBar.lbs;
      const { plates: calculatedPlates } = calculatePlates(currentWeight, weightUnit, defaultBarWeight);
      setLocalPlates(calculatedPlates);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  const handlePlateChange = useCallback((size, delta) => {
    setLocalPlates(prev => {
      const currentCount = prev[size] || 0;
      const newCount = Math.max(0, currentCount + delta);
      const newPlates = { ...prev };
      
      if (newCount === 0) {
        delete newPlates[size];
      } else {
        newPlates[size] = newCount;
      }
      
      return newPlates;
    });
  }, []);

  const handleBarChange = useCallback((newBarId) => {
    setSelectedBarId(newBarId);
  }, []);

  // Calculate total from local plates for display
  const displayTotal = useMemo(() => 
    calculateTotalFromPlates(localPlates, barWeight),
    [localPlates, barWeight]
  );

  // Calculate plates subtotal (without bar)
  const platesSubtotal = useMemo(() => {
    let total = 0;
    for (const [size, count] of Object.entries(localPlates)) {
      total += parseFloat(size) * count;
    }
    return total;
  }, [localPlates]);

  // Call onClose when component unmounts (handles X button, Escape key, back button)
  useEffect(() => {
    return () => {
      if (onClose) {
        onClose();
      }
    };
  }, [onClose]);

  const handleAccept = useCallback(() => {
    // Save configuration to localStorage
    if (fullStorageKey) {
      try {
        localStorage.setItem(fullStorageKey, JSON.stringify({
          plates: localPlates,
          barId: selectedBarId
        }));
      } catch (e) {
        // Ignore localStorage errors
      }
    }
    
    // Call onAccept with the calculated total
    onAccept(displayTotal);
    
    // Close modal first, then call onClose after modal is fully closed
    close();
  }, [fullStorageKey, localPlates, selectedBarId, displayTotal, onAccept, close]);

  const handleCancel = useCallback(() => {
    // Close modal first, then call onClose after modal is fully closed
    close();
  }, [close]);

  return (
    <div className="plate-calculator" data-unit-kind="weight">
      <div className="plate-calculator-header">
        <span className="plate-calculator-title">Plate Calculator</span>
        <span className="plate-calculator-total">
          {formatWeight(displayTotal)} {weightUnit}
        </span>
      </div>
      
      <div className="plate-calculator-bar-section">
        <label className="bar-label">Bar Type</label>
        <select 
          className="bar-select"
          value={selectedBarId}
          onChange={(e) => handleBarChange(e.target.value)}
        >
          {BAR_TYPES.map(bar => (
            <option key={bar.id} value={bar.id}>
              {bar.name} ({weightUnit === 'kg' ? bar.kg : bar.lbs} {weightUnit})
            </option>
          ))}
        </select>
      </div>

      <div className="plate-calculator-breakdown">
        <div className="breakdown-row">
          <span className="breakdown-label">{selectedBar.name}</span>
          <span className="breakdown-value">{barWeight} {weightUnit}</span>
        </div>
        <div className="breakdown-row">
          <span className="breakdown-label">Plates</span>
          <span className="breakdown-value">+ {formatWeight(platesSubtotal)} {weightUnit}</span>
        </div>
        <div className="breakdown-row breakdown-total">
          <span className="breakdown-label">Total</span>
          <span className="breakdown-value">= {formatWeight(displayTotal)} {weightUnit}</span>
        </div>
      </div>
      
      <div className="plate-calculator-plates">
        {plateSizes.map(size => {
          const count = localPlates[size] || 0;
          const subtotal = size * count;
          return (
            <div key={size} className={`plate-row ${count > 0 ? 'has-plates' : ''}`}>
              <button 
                className="plate-btn plate-btn-down"
                onClick={() => handlePlateChange(size, -1)}
                disabled={count === 0}
                aria-label={`Remove ${size} ${weightUnit} plate`}
              >
                −
              </button>
              
              <div className="plate-info">
                <span className="plate-size">{size}</span>
                <span className="plate-count">×{count}</span>
                {count > 0 && (
                  <span className="plate-subtotal">= {formatWeight(subtotal)}</span>
                )}
              </div>
              
              <button 
                className="plate-btn plate-btn-up"
                onClick={() => handlePlateChange(size, 1)}
                aria-label={`Add ${size} ${weightUnit} plate`}
              >
                +
              </button>
            </div>
          );
        })}
      </div>

      <div className="plate-calculator-actions">
        <button className="plate-calc-btn plate-calc-btn-cancel" onClick={handleCancel}>
          Cancel
        </button>
        <button className="plate-calc-btn plate-calc-btn-accept" onClick={handleAccept}>
          Accept
        </button>
      </div>
    </div>
  );
};

export default PlateCalculator;
